import { useEffect } from 'react';
import { wakeCallables, type WakeConfig, type WakeHandle } from '../wake/wakeCallables';

/**
 * Wake the callables a screen may use: once on mount, and again (throttled) when the device comes
 * back online or the app returns to the foreground -- the moments a phone's functions have usually
 * gone back to sleep. Silent: never renders, never throws, never knocks offline.
 *
 * `names` is compared by content, so an inline array literal does not retrigger the effect.
 */
export function useWakeCallables(names: readonly string[], config: WakeConfig): void {
  const key = JSON.stringify(names);
  const { urlFor, enabled, isOnline, fetchImpl, now, store, throttleMs, staggerMs, maxNames } = config;

  useEffect(() => {
    const cfg: WakeConfig = { urlFor, enabled, isOnline, fetchImpl, now, store, throttleMs, staggerMs, maxNames };
    const list: string[] = JSON.parse(key);
    // A restored background tab must not cost a dozen cold starts for a screen nobody is looking at;
    // the visibilitychange handler below knocks when it is actually shown.
    const knock = (): WakeHandle =>
      document.visibilityState === 'hidden' ? { scheduled: [], cancel: () => {} } : wakeCallables(list, cfg);
    let handle = knock();
    const again = () => { handle.cancel(); handle = knock(); };
    const onVisible = () => { if (document.visibilityState === 'visible') again(); };

    window.addEventListener('online', again);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', again);
      document.removeEventListener('visibilitychange', onVisible);
      handle.cancel();
    };
  }, [key, urlFor, enabled, isOnline, fetchImpl, now, store, throttleMs, staggerMs, maxNames]);
}
